import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { VendorDocument } from 'app/models/entities/vendors/vendor-document';
import { VendorCustomField } from '../../models/entities/vendors/vendor-custom-field';
import { CUSTOM_FIELDS_TYPE, VENDOR_FIELDS_TYPE, VendorFieldsGroup } from '../../models/entities/vendors/vendor-fields-group';
import { FieldCategoryGroups } from '../../enums/field-category-groups.enum';
import { VendorOverview } from '../../models/entities/vendors/vendor-overview';

export interface VendorStreams
{
    loaded$: BehaviorSubject<boolean>;
    loading$: BehaviorSubject<boolean>;
    vendorOverview$: BehaviorSubject<VendorOverview>;
    vendorFields$: Observable<VendorFieldsGroup[]>;
}

@Injectable({
    providedIn: 'root',
})
export class VendorService
{
    private _vendorOverview: { [orgId: number]: VendorOverview } = {};
    private _orgStreams: {
        [orgId: number]: VendorStreams
    } = {};

    constructor(
            private _http: ApplicationHttpClient) {
    }

    getOrganizationStreams(organizationId: number) {
        if (this._orgStreams[organizationId]) {
            return this._orgStreams[organizationId];
        }

        const vendorOverview$ = new BehaviorSubject<VendorOverview>({vendor: [], vendorCustomFields: []});
        const streams = {
            loaded$: new BehaviorSubject<boolean>(false),
            loading$: new BehaviorSubject<boolean>(false),
            vendorOverview$: vendorOverview$,
            vendorFields$: vendorOverview$.pipe(
                    map((data) => {
                        const fields: VendorFieldsGroup[] = [];
                        if (data.vendor.length > 0) {
                            fields.push({
                                name: FieldCategoryGroups.VENDOR,
                                options: data.vendor.map((opt: { key: string; label: string; }) => {
                                    return {
                                        label: opt.label,
                                        value: opt.key,
                                    };
                                }),
                                category: VENDOR_FIELDS_TYPE,
                            });
                        }
                        if (data.vendorCustomFields.length > 0) {
                            fields.push({
                                name: FieldCategoryGroups.CUSTOM,
                                options: data.vendorCustomFields.map((opt: { key: string; label: string; }) => {
                                    return {
                                        label: opt.label,
                                        value: opt.key,
                                    };
                                }),
                                category: CUSTOM_FIELDS_TYPE,
                            });
                        }
                        return fields;
                    }),
            ),
        };
        this._orgStreams[organizationId] = streams;
        return streams;
    }

    /**
     * Get List of Vendors for an org
     * @param {number} organizationId
     * @param withs
     * @returns {Observable<Vendor[]>}
     */
    getOrgVendorList(organizationId: number, withs: string[] = []): Observable<Vendor[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.BillPayVendors(organizationId || jwt.orgId),
        ) + (withs.length ? '?with=' + withs.join(',') : '');

        return this._http.get(url).pipe(map(
                (response: Object[]): Vendor[] => {
                    return response.map(
                            (vendor) => {
                                return new Vendor(vendor);
                            },
                    );
                },
        ));
    }

    /**
     * Get a list of documents for a vendor
     * @param {number} organizationId
     * @param {number} vendorId
     * @returns {Observable<VendorDocument[]>}
     */
    getOrgVendorDocuments(organizationId: number, vendorId: number): Observable<VendorDocument[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.BillPayVendorAttachments(organizationId || jwt.orgId, vendorId)),
        ).pipe(map(
                (response: Object[]): VendorDocument[] => {
                    return response.map(
                            (vendor) => {
                                return new VendorDocument(vendor);
                            },
                    );
                },
        ));
    }

    renameOrgVendorDocument(
            organizationId: number,
            vendorId: number,
            documentId: number,
            name: string,
    ): Observable<VendorDocument> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.BillPayVendorDocument(organizationId || jwt.orgId, vendorId, documentId),
        );

        return this._http.patch(url, JSON.stringify({name: name})).pipe(map(
                (response: Object): VendorDocument => {
                    return new VendorDocument(response);
                },
        ));
    }

    deleteOrgVendorDocument(organizationId: number, vendorId: number, documentId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.BillPayVendorDocument(organizationId || jwt.orgId, vendorId, documentId),
        );

        return this._http.delete(url);
    }

    /**
     * Gets an AWS signed url for the provided document
     * @param organizationId
     * @param vendorId
     * @param documentId
     * @param inline
     */
    getOrgVendorDocumentLink(
            organizationId: number,
            vendorId: number,
            documentId: number,
            inline = false,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.BillPayVendorAttachmentLink(organizationId || jwt.orgId, vendorId, documentId),
        );

        if (inline) {
            url = url + '?inline=true';
        }

        return this._http.get(url);
    }

    /**
     * Get details of one Org Vendor
     * @param {number} organizationId
     * @param {number} vendorId
     * @returns {Observable<Vendor[]>}
     */
    getOrgVendorDetails(organizationId: number, vendorId: number): Observable<Vendor> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.BillPayVendor(organizationId || jwt.orgId, vendorId)),
        ).pipe(map(
                (response: Object): Vendor => {
                    return new Vendor(response);
                },
        ));
    }

    getValidFieldsForVendorOverview() {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ReportsVendorOverviewFields(jwt.orgId));
        const streams = this.getOrganizationStreams(jwt.orgId);
        streams.loading$.next(true);
        return this._http.get(url).pipe(map((response: VendorOverview) => {
            this._vendorOverview[jwt.orgId] = response;
            streams.vendorOverview$.next(response);
            streams.loading$.next(false);
            streams.loaded$.next(true);
            return response;
        }));
    }

    getOrgVendorCustomFields(organizationId: number, vendorId: number): Observable<VendorCustomField[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.CustomFieldValuesForVendor(organizationId || jwt.orgId, vendorId)),
        ).pipe(map(
                (response: Object[]): VendorCustomField[] => {
                    return response.map(
                            (vendor) => {
                                return new VendorCustomField(vendor);
                            },
                    );
                },
        ));
    }

    /**
     * Create new org vendor
     * @param {number} organizationId
     * @param body
     * @returns {Observable<Vendor>}
     */
    createOrgVendor(organizationId: number, body: any): Observable<Vendor> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.BillPayVendors(organizationId || jwt.orgId),
                ), JSON.stringify(body),
        ).pipe(map(
                (response: Object): Vendor => {
                    return new Vendor(response);
                },
        ));
    }

    /**
     * Create new org vendor
     * @param {number} organizationId
     * @param vendorId
     * @param body
     * @returns {Observable<Vendor>}
     */
    saveVendorCustomFields(organizationId: number, vendorId: number, body: any): Observable<Vendor> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.CustomFieldValuesForVendor(organizationId || jwt.orgId, vendorId),
                ), JSON.stringify(body),
        ).pipe(map(
                (response: Object): Vendor => {
                    return new Vendor(response);
                },
        ));
    }

    /**
     * Update one org vendor details
     * @param {number} organizationId
     * @param {number} vendorId
     * @param body
     * @returns {Observable<Vendor>}
     */
    updateOrgVendor(organizationId: number, vendorId: number, body: any): Observable<Vendor> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.BillPayVendor(organizationId || jwt.orgId, vendorId),
                ), JSON.stringify(body),
        ).pipe(map(
                (response: Object): Vendor => {
                    return new Vendor(response);
                },
        ));
    }

    /**
     * Delete org vendor
     * @param {number} organizationId
     * @param {number} vendorId
     * @returns {Observable<Object>}
     */
    deleteOrgVendor(organizationId: number, vendorId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.delete(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.BillPayVendor(organizationId || jwt.orgId, vendorId)),
        );
    }

    /**
     * Delete org vendor
     * @param {number} vendorId
     * @returns {Observable<Object>}
     */
    deleteOrgVendorLogo(vendorId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.delete(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.BillPayVendor(jwt.orgId, vendorId)) + '/logo',
        );
    }

}
